import { CustomEase, gsap, TimelineLite } from 'gsap';

import { crystalTypes, wheelResource, wheelResourceNames } from '../../../core/gameConstants';
import { BaseComponentDrawer } from '../../../core/abstraction/BaseComponentDrawer';
import { calculateDegree } from '../../../core/helpers/calculateDegree';
import { gameConfig, mathConfig } from '../../../core/configs';
import { spinResponseHelper } from '../../../services/helperService/SpinResponseHelper';
import { GAME_MODE } from '../../../stateManagment/gameState';
import { backgroundDrawer } from '../../background/BackgroundDrawer';
import { FONT_BALOO_NAME, FONT_LATO_NAME } from '../../../core/constats';
import { crystalPerTypeHelper } from '../../../services/helperService/CrystalPerTypeHelper';
import { bonusSpinResponseHelper } from '../../../services/helperService/BonusSpinResponseHelper';
import { amountFormatter } from '../../../core/helpers/amountFormatter';
import { soundService } from '../../../services/helperService/PixiSoundService';
import { autoSpinHelper } from '../../../services/helperService/AutoSpinHelper';
import {getTranslation, translationKeys} from '../../../core/translation/translation';
import { partnerService } from '../../../services/helperService/PartnerService';
import { delay } from '../../../core/helpers/delay';
import { animationDrawer } from '../../../core/AnimationDrawer';
import { spinButtonHelper } from '../../../services/helperService/SpinButtonHelper';
import {gameDrawer} from "../../../game/GameDrawer";

const { halfPI, doublePI } = mathConfig;

export class BaseWheelDrawer extends BaseComponentDrawer {
  constructor() {
    super();
    this.loader = this.createLoader();
    this.lightElement = null;
    this.wheelContainer = this.createContainer('wheelContainer');
    this.winContainer = this.createContainer('winContainer');
    this.config = {
      titleStyle: {
        fill: [0xfefefd, 0x9b7853],
        fontFamily: FONT_LATO_NAME,
        stroke: '#dec195',
        strokeThickness: 1,
        fontSize: 24,
        align: 'center',
      },
    };

    this.rotateAnimationStarted = true;
  }

  getWheel({
    x,
    y,
    radius,
    crystalCount,
    imageScale = .20,
    crystals = crystalPerTypeHelper.nativeCrystals,
    scale,
  }) {
    this.wheelContainer.children = [];
    const circleContainer = this.createContainer('circleContainer');

    const bonusMarker = this.createSprite(this.resources[wheelResourceNames.bonusMarker]);
    bonusMarker.anchor.set(.5);
    bonusMarker.scale.set(scale);
    bonusMarker.angle = 50.625;

    this.setPosition(this.wheelContainer, { x, y });

    const circle = new this.PIXI.Graphics()
      .drawCircle(
        0,
        0,
        radius,
      ).endFill();
    const rectWidth = 24;
    circleContainer.addChild(
      bonusMarker,
      circle,
    );
    crystals.forEach((name, i) => {
      const alpha = (doublePI / crystalCount * i) - halfPI;
      const position_x = x + radius * Math.cos(alpha);
      const position_y = y + radius * Math.sin(alpha);

      const crystalContainer = new this.PIXI.Container();
      crystalContainer.x = position_x;
      crystalContainer.y = position_y;
      crystalContainer.rotation = halfPI + alpha;
      const rect = new this.PIXI.Graphics()
        .drawRect(0, 0, rectWidth, 30)
        .endFill();
      const image = this.createSprite(this.resources[crystalTypes[name]]);
      image.anchor.set(.5);
      image.scale.set(imageScale);
      rect.addChild(image);
      crystalContainer.addChild(rect);
      circle.addChild(crystalContainer);
    });
    this.wheelContainer.addChild(circleContainer);

    return { circle: circleContainer, container: this.wheelContainer };
  }

  getWheelTexture(config = {}, isDark) {
    const { scale = 1 } = config;
    const wheelTexture = this.createSprite(this.resources[isDark ? wheelResourceNames.wheelDark : wheelResourceNames.wheel]);
    wheelTexture.anchor.set(.5);
    wheelTexture.scale.set(scale);
    return wheelTexture;
  }

  getWheelLight(config = {}) {
    const { scale = 1 } = config;
    const mainWheelLight = this.createSprite(this.resources[wheelResourceNames.mainWheelLight]);
    mainWheelLight.anchor.set(.5);
    mainWheelLight.scale.set(scale);
    this.lightElement = mainWheelLight;
    return mainWheelLight;
  }

  getGoldenRingTexture(config = {}) {
    const { scale = 1 } = config;
    const goldenRingTexture = this.createSprite(this.resources[wheelResourceNames.wheelRing]);
    goldenRingTexture.anchor.set(.5);
    goldenRingTexture.scale.set(scale);
    return goldenRingTexture;
  }

  getGoldenElementTexture(config = {}) {
    const { scale = 1 } = config;
    const texture = this.createSprite(this.resources[wheelResourceNames.mainWheelCrown]);
    texture.anchor.set(.5);
    texture.scale.set(scale);
    return texture;
  }

  getAnimation(gems) {
    const { animations } = backgroundDrawer.applications[GAME_MODE.getValue()];
    const color = gems.split('_')[1].trim();
    if (animations[color]) {
      return animations[color];
    }

    return animations.glow;
  }

  _rotateStopCase({ texture, deg, onComplete }) {
    const { fullCircleDegree } = gameConfig.wheel;
    const rotationCount = Math.floor(texture.angle / fullCircleDegree);
    const duration = .2;

    let angle = deg >= texture.angle ? deg : fullCircleDegree * (rotationCount || 1) + deg;

    if (angle < texture.angle) {
      const angleDifference = texture.angle - angle;
      angle += Math.floor(angleDifference) * fullCircleDegree;
    }

    gsap.fromTo(
      texture,
      {
        angle: texture.angle,
      },
      {
        ease: CustomEase.create(
          'rotate',
          'M0,0,C0.126,0.382,0.282,0.674,0.44,0.822,0.632,1.002,0.818,1.001,1,1',
        ),
        duration,
        angle,
        onComplete,
      },
    );
  }

  _isStopCheck(helper) {
    return helper === spinResponseHelper && spinResponseHelper.isStop && !autoSpinHelper.isAutoSpin;
  }

  __getRotation(texture, angle) {
    const { fullCircleDegree } = gameConfig.wheel;
    let rotation = angle;
    if (angle < texture.angle) {
      rotation += fullCircleDegree;
    }
    return rotation;
  }

  rotateCircle(circle, onEnd, helper) {
    const self = this;
    const { fullCircleDegree } = gameConfig.wheel;
    if (helper.hasError) {
      soundService.stopWheel();
      gsap.to(
        circle, {
          duration: 0,
          angle: self.__getRotation(circle,((Math.floor(circle.angle / fullCircleDegree) || 1) + 1) * fullCircleDegree),
          onComplete: () => {
            circle.angle = 0;
            onEnd();
          },
        });
      return;
    }

    if (helper.notNull()) {
      const { result } = helper.getData();
      const num = helper.getRandomNum(result);
      const color = crystalTypes[crystalPerTypeHelper.crystals[num]];
      const colorAnimation = this.getAnimation(color);
      const rotation = calculateDegree(num);

      if (self._isStopCheck(helper)) {
        this._rotateStopCase({
          texture: circle,
          deg: self.__getRotation(circle,rotation),
          onComplete: () => {
            circle.angle = rotation;
            onEnd();
            colorAnimation();
            this.rotateAnimationStarted = true;
            soundService.stopWheel();
            soundService.playWheelStopOnCrystal();
          },
        });
        return;
      }

      gsap.to(
        circle,
        {
          duration: self._isStopCheck(helper) ? .4 : 3,
          ease: CustomEase.create(
            'rotate',
            'M0,0,C0.126,0.382,0.282,0.674,0.44,0.822,0.632,1.002,0.818,1.001,1,1',
          ),
          angle: self.__getRotation(circle, fullCircleDegree + rotation),
          onUpdate: function () {
            if (self._isStopCheck(helper)) {
              this.kill();

              let overrideNum = num;
              let overrideColor = color;
              let overrideColorAnimation = colorAnimation;
              let overrideRotation = rotation;

              const isBadResult = result !== helper.getData()?.result;
              if (isBadResult && helper.getData()?.result) {
                overrideNum = helper.getRandomNum(helper.getData().result);
                overrideColor = crystalTypes[crystalPerTypeHelper.crystals[overrideNum]];
                overrideColorAnimation = self.getAnimation(overrideColor);
                overrideRotation = calculateDegree(overrideNum);
              }

              overrideRotation = self.__getRotation(circle, overrideRotation)

              self._rotateStopCase({
                texture: circle,
                deg: overrideRotation,
                onComplete: () => {
                  circle.angle = overrideRotation;
                  onEnd();
                  overrideColorAnimation();
                  this.rotateAnimationStarted = true;
                  soundService.stopWheel();
                  soundService.playWheelStopOnCrystal();
                },
              });
            }
          },
          onComplete: () => {
            circle.angle = rotation;
            onEnd();
            colorAnimation();
            this.rotateAnimationStarted = true;
            soundService.stopWheel();
            soundService.playWheelStopOnCrystal();
          },
        });
    } else {
      const startedAngle = circle.angle % 360;
      gsap.to(
        circle,
        {
          duration: .8,
          ease: this.rotateAnimationStarted ? 'power2.in' : 'none',
          angle: startedAngle + fullCircleDegree,
          onUpdate: function () {
            if (self._isStopCheck(helper) && helper.notNull()) {
              this.kill();
              const { result } = helper.getData();
              const num = helper.getRandomNum(result);
              const color = crystalTypes[crystalPerTypeHelper.crystals[num]];
              const colorAnimation = self.getAnimation(color);
              const deg = calculateDegree(num);

              self._rotateStopCase({
                texture: circle,
                deg,
                onComplete: () => {
                  circle.angle = deg;
                  onEnd();
                  colorAnimation();
                  this.rotateAnimationStarted = true;
                  soundService.stopWheel();
                  soundService.playWheelStopOnCrystal();
                },
              });
            }
          },
          onComplete: () => {
            circle.angle = startedAngle;
            this.rotateAnimationStarted = false;
            this.rotateCircle(circle, onEnd, helper);
          },
        });
    }
  }

  setupWheelLightAnimation({ texture }) {
    const animation = new TimelineLite();
    animation
      .to(texture, .2, { alpha: 1 })
      .to(texture, .2, { alpha: .9 })
      .to(texture, .2, { alpha: 1 })
      .to(texture, .2, { alpha: .9 })
      .to(texture, .2, { alpha: 1 })
      .to(texture, .2, { alpha: .9 })
      .to(texture, .2, { alpha: 1 })
      .to(texture, .2, { alpha: .9 });
  }

  winAnimation(data) {
    this.winContainer.children = [];
    spinResponseHelper.nullify();
    if (data && data.winAmount) {
      const { winAmount } = data;
      this.winContainer.addChild(
        this.getOpacityCircle(),
        this.getWinAmountAnimation({ amount: winAmount }),
      );
      this.setupWheelLightAnimation({ texture: this.lightElement });
    } else {
      this.winContainer.addChild(this.getNoWin());
    }
  }

  getGoodLuckAnimation() {
    this.winContainer.children = [];
    this.winContainer.addChild(this.getGoodLuck());
  }

  bonusWinAnimation() {
    this.winContainer.children = [];
    const data = bonusSpinResponseHelper.getData();
    if (data && data.winAmount) {
      const { nativeWinAmount, winAmount } = data;
      const content = this.getWinAmountAnimation({ amount: nativeWinAmount });
      this.winContainer.addChild(
        this.getOpacityCircle(),
        content,
      );

      delay(() => {
        animationDrawer.blinking(content.children[0], .2, [.9, 1.2, 1]);
        content.children[0].text = `${amountFormatter.format(winAmount)} ${partnerService.displayCurrency}`;
      }, 1000);
      this.setupWheelLightAnimation({ texture: this.lightElement });
    } else {
      this.winContainer.addChild(this.getNoWin());
    }
  }

  getGoodLuck() {
    const content = this.createContainer();
    const goodLuck = this.createSprite(this.resources[wheelResourceNames.goodLuck]);
    goodLuck.alpha = 0;
    gsap.to(goodLuck, { duration: 1, pixi: { alpha: 1 } });
    goodLuck.anchor.set(.5);
    content.addChild(
      goodLuck,
    );
    return content;
  }

  getPressToContinue(scale = 1.3) {
    const content = this.createContainer();
    const pressToContinue = this.createSprite(this.resources[wheelResourceNames.pressToContinue]);
    pressToContinue.anchor.set(.5);
    this.addSubscriber(spinButtonHelper.subscribe((active, { prev }) => {
      if (prev === false && active) {
        gsap.to(pressToContinue, { duration: .05, pixi: { alpha: 1, scale } });
      } else {
        pressToContinue.alpha = .5;
        pressToContinue.scale.set(0);
      }
    }, true));
    content.addChild(
      pressToContinue,
    );
    return content;
  }

  getNoWin() {
    const content = this.createContainer();
    const noWin = this.createSprite(this.resources[wheelResourceNames.noWinImage]);
    noWin.alpha = 0;
    gsap.to(noWin, { duration: 1, pixi: { alpha: 1 } });
    noWin.anchor.set(.5);
    content.addChild(
      noWin,
    );
    return content;
  }

  getWinAmountAnimation({ amount }) {
    const content = this.createContainer();
    let fontSize = 53.5;
    const amountLength = String(amount).length;
    if (amountLength >= 7) {
      fontSize = 42;
    }
    if (amountLength >= 8) {
      fontSize = 38;
    }
    if (amountLength >= 9) {
      fontSize = 35;
    }
    if (amountLength >= 9) {
      fontSize = 30;
    }
    const amountText = new this.PIXI.Text(`${amountFormatter.format(amount)} ${partnerService.displayCurrency}`, {
      fill: [0xf6e2cd, 0xb38758, 0xb58f66],
      fontWeight: 900,
      fillGradientStops: [.33, .80, 1],
      fontFamily: FONT_BALOO_NAME,
      stroke: 0xf6e2cd,
      strokeThickness: 1,
      fontSize,
      align: 'center',
      dropShadow: true,
      dropShadowColor: 0x221605,
      dropShadowAlpha: .58,
      dropShadowAngle: Math.PI / 2,
      dropShadowBlur: 9,
      dropShadowDistance: 8,
    });

    const winText = new this.PIXI.Text(`${getTranslation(translationKeys.WIN)}`, {
      fill: [0xf6e2cd, 0xb38758, 0xb58f66],
      fontWeight: 900,
      fillGradientStops: [.33, .80, 1],
      fontFamily: FONT_LATO_NAME,
      stroke: 0xf6e2cd,
      strokeThickness: 1,
      fontSize: 39.5,
      align: 'center',
      dropShadow: true,
      dropShadowColor: 0x221605,
      dropShadowAlpha: .58,
      dropShadowAngle: Math.PI / 2,
      dropShadowBlur: 9,
      dropShadowDistance: 8,
    });

    amountText.anchor.set(.5);
    winText.anchor.set(.5);

    this.setPosition(amountText, { y: amountText.height / 2 - 10 });
    this.setPosition(winText, { y: -amountText.height / 2 + 10 });
    content.alpha = 0;
    gsap.to(content, { duration: 1, pixi: { alpha: 1 } });
    content.addChild(
      amountText,
      winText,
    );
    return content;
  }

  getOpacityCircle({ radius = 240 } = {}) {
    const circle = this.createGraphics()
      .beginFill(0x161616)
      .drawCircle(0, 0, radius)
      .endFill();
    circle.alpha = .65;
    return circle;
  }

  async loadResource(rss = []) {
    const { res } = await super.loadResource([...wheelResource, ...rss], this.loader);
    this.resources = res;
    return this.resources;
  }
}
